import React, { useState, useEffect } from 'react';
import { UserPlus, FileText, Users, Clock, Plus, Download } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import EmptyState from '../../components/EmptyState';
import Button from '../../components/ui/Button';

import OnboardingTemplates from './components/OnboardingTemplates';
import ActiveOnboardingCases from './components/ActiveOnboardingCases';
import OnboardingStats from './components/OnboardingStats';
import TemplateBuilder from './components/TemplateBuilder';
import { onboardingApi } from '../../lib/api';

const EmployeeOnboarding = () => {
  const [templates, setTemplates] = useState([]);
  const [onboardingCases, setOnboardingCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview'); // 'overview', 'templates', 'cases'
  const [showTemplateBuilder, setShowTemplateBuilder] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // Load data on component mount
  useEffect(() => {
    loadOnboardingData();
  }, []);

  const loadOnboardingData = async () => {
    try {
      setLoading(true);
      const [templatesData, casesData] = await Promise.all([
        onboardingApi?.getTemplates(),
        onboardingApi?.getCases()
      ]);
      setTemplates(templatesData);
      setOnboardingCases(casesData);
    } catch (err) {
      setError(err?.message);
      // Set empty arrays for demo
      setTemplates([]);
      setOnboardingCases([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = () => {
    setSelectedTemplate(null);
    setShowTemplateBuilder(true);
  };

  const handleEditTemplate = (template) => {
    setSelectedTemplate(template);
    setShowTemplateBuilder(true);
  };

  const handleSaveTemplate = async (templateData) => {
    try {
      if (selectedTemplate) {
        // Update existing template
        await onboardingApi?.updateTemplate(selectedTemplate?.id, templateData);
      } else {
        // Create new template
        await onboardingApi?.createTemplate(templateData);
      }
      setShowTemplateBuilder(false);
      loadOnboardingData();
    } catch (err) {
      console.error('Error saving template:', err);
    }
  };

  const handleCreateOnboardingCase = () => {
    // TODO: Open new hire onboarding case creation
    console.log('Create onboarding case clicked');
  };

  const handleExportChecklist = (caseId) => {
    // TODO: Export onboarding checklist as PDF
    console.log('Export checklist for case:', caseId);
  };

  const pageHeaderActions = [
    {
      label: 'Import Template',
      icon: Download,
      variant: 'outline',
      onClick: () => console.log('Import template')
    },
    {
      label: 'Create Template',
      icon: Plus,
      onClick: handleCreateTemplate
    }
  ];

  // Show API not connected banner
  const apiNotConnected = error && error?.includes('Not implemented');

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Employee Onboarding"
        subtitle="Create and manage comprehensive onboarding workflows for new hires"
        actions={pageHeaderActions}
        breadcrumbs={[
          { label: 'HR', href: '/' },
          { label: 'Onboarding' }
        ]}
      />
      {apiNotConnected && (
        <div className="mx-6 mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-amber-800">
                Backend not configured
              </h3>
              <p className="text-sm text-amber-700 mt-1">
                Set VITE_API_URL and wire endpoints to enable onboarding data.
              </p>
            </div>
          </div>
        </div>
      )}
      <div className="px-6 py-6">
        {/* Tab Navigation */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'overview' ?'border-blue-500 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                onClick={() => setActiveTab('overview')}
              >
                Overview
              </button>
              <button
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'templates' ?'border-blue-500 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                onClick={() => setActiveTab('templates')}
              >
                Templates
              </button>
              <button
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'cases' ?'border-blue-500 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                onClick={() => setActiveTab('cases')}
              >
                Active Cases
              </button>
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Onboarding Stats */}
            <OnboardingStats 
              templates={templates}
              onboardingCases={onboardingCases}
            />

            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Button
                  onClick={handleCreateTemplate}
                  className="justify-start h-auto p-4"
                  variant="outline"
                >
                  <FileText className="h-5 w-5 mr-3" />
                  <div className="text-left">
                    <div className="font-medium">Create Template</div>
                    <div className="text-sm text-gray-500">Build a new onboarding workflow</div>
                  </div>
                </Button>
                <Button
                  onClick={handleCreateOnboardingCase}
                  className="justify-start h-auto p-4"
                  variant="outline"
                >
                  <UserPlus className="h-5 w-5 mr-3" />
                  <div className="text-left">
                    <div className="font-medium">Start Onboarding</div>
                    <div className="text-sm text-gray-500">Begin new hire process</div>
                  </div>
                </Button>
                <Button
                  onClick={() => setActiveTab('cases')}
                  className="justify-start h-auto p-4"
                  variant="outline"
                >
                  <Clock className="h-5 w-5 mr-3" />
                  <div className="text-left">
                    <div className="font-medium">Track Progress</div>
                    <div className="text-sm text-gray-500">Monitor active onboarding</div>
                  </div>
                </Button>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Activity</h3>
              {onboardingCases?.length === 0 ? (
                <EmptyState
                  icon={Clock}
                  title="No recent activity"
                  description="Onboarding activity will appear here once you start new hire processes."
                  primaryAction={{
                    label: 'Start Onboarding',
                    icon: UserPlus,
                    onClick: handleCreateOnboardingCase
                  }}
                  secondaryAction={undefined}
                />
              ) : (
                <div className="space-y-3">
                  {onboardingCases?.slice(0, 5)?.map((onboardingCase) => (
                    <div key={onboardingCase?.id} className="flex items-center justify-between py-2">
                      <div className="flex items-center">
                        <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                          <Users className="h-4 w-4 text-blue-600" />
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">
                            New hire onboarding progress
                          </div>
                          <div className="text-sm text-gray-500">
                            {onboardingCase?.completionPercentage || 0}% complete
                          </div>
                        </div>
                      </div>
                      <div className="text-sm text-gray-500">
                        {onboardingCase?.startDate ? new Date(onboardingCase.startDate)?.toLocaleDateString() : 'N/A'}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'templates' && (
          <OnboardingTemplates
            templates={templates}
            onCreateTemplate={handleCreateTemplate}
            onEditTemplate={handleEditTemplate}
            onDeleteTemplate={(id) => console.log('Delete template:', id)}
          />
        )}

        {activeTab === 'cases' && (
          <ActiveOnboardingCases
            onboardingCases={onboardingCases}
            onCreateCase={handleCreateOnboardingCase}
            onExportChecklist={handleExportChecklist}
            onUpdateTaskStatus={(taskId, completed) => console.log('Update task:', taskId, completed)}
          />
        )}
      </div>
      {/* Template Builder Modal */}
      {showTemplateBuilder && (
        <TemplateBuilder
          template={selectedTemplate}
          onSave={handleSaveTemplate}
          onCancel={() => setShowTemplateBuilder(false)}
        />
      )}
    </div>
  );
};

export default EmployeeOnboarding;